import React, { useEffect, useMemo, useState } from 'react';
import { fetchLogList } from '../../services/logApi';
import './LogManagement.css';

const PAGE_SIZE = 10;
const PAGE_GROUP_SIZE = 5;

export default function Log({ onTotalChange }) {
  const [logList, setLogList] = useState([]);
  const [inputQuery, setInputQuery] = useState('');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getLogs = async () => {
      try {
        const logs = await fetchLogList();
        setLogList(logs);
        onTotalChange?.(logs.length);
      } catch (error) {
        console.error('로그 요청 실패:', error);
        alert('로그 목록을 가져오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    getLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    const keyword = query.toLowerCase();

    if (!keyword) return logList;

    return logList.filter((log) =>
      [
        log.admin_name,
        log.admin_username,
        log.content,
        log.username,
        log.ip,
        log.create_date
      ].some((value) =>
        String(value ?? '').toLowerCase().includes(keyword)
      )
    );
  }, [logList, query]);

  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE);
  const currentLogs = filteredLogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const currentGroup = Math.floor((currentPage - 1) / PAGE_GROUP_SIZE);
  const startPage = currentGroup * PAGE_GROUP_SIZE + 1;
  const endPage = Math.min(startPage + PAGE_GROUP_SIZE - 1, totalPages);

  const handleSearch = () => {
    setQuery(inputQuery.trim());
    setCurrentPage(1);
  };

  const handleReset = () => {
    setInputQuery('');
    setQuery('');
    setCurrentPage(1);
  };

  return (
    <section className="lm-section">
      <div className="lm-toolbar">
        <div className="lm-search">
          <input
            type="text"
            placeholder="이름, 아이디, 작업내용, IP주소 검색"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                handleSearch();
              }
            }}
          />
          <button type="button" onClick={handleSearch}>
            검색
          </button>
          <button type="button" className="lm-reset" onClick={handleReset}>
            초기화
          </button>
        </div>
      </div>

      <div className="lm-table-top">
        <strong>조회 결과 {filteredLogs.length.toLocaleString()}건</strong>
        <span>시스템 작업 이력 확인</span>
      </div>

      <div className="lm-table-wrap">
        <table className="lm-table">
          <colgroup>
            <col style={{ width: '5%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '19%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>No</th>
              <th>이름</th>
              <th>아이디</th>
              <th>작업내용</th>
              <th>작업대상</th>
              <th>IP주소</th>
              <th>일시</th>
            </tr>
          </thead>
          <tbody>
            {currentLogs.length > 0 ? (
              currentLogs.map((log, index) => (
                <tr key={`${log.id ?? currentPage}-${index}`}>
                  <td>{filteredLogs.length - (currentPage - 1) * PAGE_SIZE - index}</td>
                  <td>{log.admin_name || '-'}</td>
                  <td>{log.admin_username || '-'}</td>
                  <td className="lm-content-cell" title={log.content || ''}>
                    {log.content || '-'}
                  </td>
                  <td>{log.username || '-'}</td>
                  <td>{log.ip || '-'}</td>
                  <td className="lm-date">{log.create_date || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="lm-empty">
                  {loading ? '로그를 불러오는 중입니다.' : '조회된 로그가 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="lm-footer">
        <span>
          총 {filteredLogs.length.toLocaleString()}건
          {totalPages > 0 && ` · ${currentPage} / ${totalPages} 페이지`}
        </span>

        {totalPages > 1 && (
          <div className="lm-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              «
            </button>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              ‹
            </button>

            {Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i
            ).map((page) => (
              <button
                key={page}
                className={currentPage === page ? 'active' : ''}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              ›
            </button>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              »
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
